import { Card, CardContent, Typography, Chip, CircularProgress, Box } from '@mui/material';
import { ErrorOutline as ErrorIcon, Replay as RetryIcon } from '@mui/icons-material';

interface ResultCardProps {
  testName: string;
  pathogen: string;
  value: number | string;
  status: 'Normal' | 'Above avg' | 'Negative' | 'Positive' | 'Analyzing' | 'Inconclusive' | 'Retrying' | 'Error';
}

export function ResultCard({ testName, pathogen, value, status }: ResultCardProps) {
  const getPathogenChipColor = () => {
    switch (status) {
      case 'Positive':
        return '#d32f2f'; // red
      case 'Negative':
      case 'Normal':
        return '#388e3c'; // green
      case 'Inconclusive':
        return '#f57c00'; // orange
      case 'Retrying':
        return '#ed6c02'; // amber/warning
      case 'Error':
        return '#d32f2f'; // red
      case 'Analyzing':
        return '#bdbdbd'; // gray
      default:
        return '#bdbdbd';
    }
  };

  const isAnalyzing = status === 'Analyzing';
  const isRetrying = status === 'Retrying';
  const isError = status === 'Error';
  const showSpinner = isAnalyzing || isRetrying;

  return (
    <Card sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 2,
      boxShadow: 2,
      opacity: showSpinner ? 0.7 : 1,
      ...(isError && { borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }),
    }}>
      <CardContent sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 3,
      }}>
        <Typography variant="overline" color="text.secondary" gutterBottom>
          {testName}
        </Typography>
        
        <Chip
          label={pathogen}
          sx={{
            mb: 2,
            backgroundColor: getPathogenChipColor(),
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.875rem',
          }}
        />
        
        {isAnalyzing ? (
          <Box sx={{ my: 3 }}>
            <CircularProgress size={48} />
          </Box>
        ) : isRetrying ? (
          <Box sx={{ my: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <RetryIcon sx={{ fontSize: 32, color: 'warning.main', animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
            <Typography variant="caption" color="warning.main">
              Retrying...
            </Typography>
          </Box>
        ) : isError ? (
          <Box sx={{ my: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
            <Typography variant="caption" color="error.main">
              Classification Failed
            </Typography>
          </Box>
        ) : (
          <Typography variant="h3" sx={{ fontWeight: 600, my: 2 }}>
            {typeof value === 'number' ? value.toFixed(2) : value}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
